import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { OnboardingKey } from '../../stores/slices/types/onboarding';

import { DISMISSAL_EXPIRY_MS, DISMISSAL_STORAGE_KEY, MAX_REMIND_LATER_COUNT } from './constants';

type OnboardingTourKey = Exclude<OnboardingKey, null>;

type PerTourState = {
  firstShownAt: string | null;
  remindLaterCount: number;
  completed: boolean;
};

type OnboardingDismissalStore = {
  byKey: Partial<Record<OnboardingTourKey, PerTourState>>;
  sessionDismissed: Partial<Record<OnboardingTourKey, boolean>>;
  markShown: (key: OnboardingTourKey) => void;
  recordRemindLater: (key: OnboardingTourKey) => void;
  markCompleted: (key: OnboardingTourKey) => void;
};

const emptyState = (): PerTourState => ({
  firstShownAt: null,
  remindLaterCount: 0,
  completed: false,
});

export const useOnboardingDismissalStore = create<OnboardingDismissalStore>()(
  persist(
    (set, get) => ({
      byKey: {},
      sessionDismissed: {},

      markShown: (key) => {
        const existing = get().byKey[key];
        if (existing?.firstShownAt) return;
        set((state) => ({
          byKey: {
            ...state.byKey,
            [key]: {
              ...(existing ?? emptyState()),
              firstShownAt: new Date().toISOString(),
            },
          },
        }));
      },

      recordRemindLater: (key) => {
        const existing = get().byKey[key] ?? emptyState();
        set((state) => ({
          byKey: {
            ...state.byKey,
            [key]: {
              ...existing,
              firstShownAt: existing.firstShownAt ?? new Date().toISOString(),
              remindLaterCount: existing.remindLaterCount + 1,
            },
          },
          sessionDismissed: { ...state.sessionDismissed, [key]: true },
        }));
      },

      markCompleted: (key) => {
        const existing = get().byKey[key] ?? emptyState();
        set((state) => ({
          byKey: {
            ...state.byKey,
            [key]: {
              ...existing,
              firstShownAt: existing.firstShownAt ?? new Date().toISOString(),
              completed: true,
            },
          },
        }));
      },
    }),
    {
      name: DISMISSAL_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byKey: state.byKey }),
    }
  )
);

export const useShouldShowOnboarding = (key: OnboardingTourKey): boolean =>
  useOnboardingDismissalStore((state) => {
    if (state.sessionDismissed[key]) return false;
    const entry = state.byKey[key];
    if (!entry) return true;
    if (entry.completed) return false;
    if (entry.remindLaterCount >= MAX_REMIND_LATER_COUNT) return false;
    if (!entry.firstShownAt) return true;
    return Date.now() - new Date(entry.firstShownAt).getTime() < DISMISSAL_EXPIRY_MS;
  });
